import * as THREE from "three";
import {OrbitControls} from "three/examples/jsm/controls/OrbitControls.js";
import {GLTFLoader} from "three/examples/jsm/loaders/GLTFLoader.js";
import {DRACOLoader} from "three/examples/jsm/loaders/DRACOLoader.js";
import {MeshoptDecoder} from "three/examples/jsm/libs/meshopt_decoder.module.js";
import {RoomEnvironment} from "three/examples/jsm/environments/RoomEnvironment.js";

/**
 * Upper limit of rendered pixels. The renderer lowers the pixel ratio for a
 * large stage, so a big viewer does not cost more than a small one.
 */
const MAX_RENDER_PIXELS = 2200000;

/**
 * Angles of the start view, in degrees. The model then stands slightly above
 * the eye of the viewer and turned to the left, as a product photo does.
 */
const START_AZIMUTH = 35;
const START_POLAR = 72;

/**
 * Distance between the camera and the model, as a part of the radius of the
 * model. A value above 1 keeps a margin between the model and the border.
 */
const FRAME_MARGIN = 1.15;

/**
 * Folder of the Draco decoder. The build copies the decoder into the folder
 * "draco" next to this bundle. The address comes from the script element
 * while the bundle runs, so every layout finds its own copy.
 */
const DRACO_DECODER_PATH = document.currentScript
    ? new URL("draco/", document.currentScript.src).href
    : "";

/**
 * One Draco loader for all viewers. It starts its workers and loads the
 * decoder only for the first model that uses Draco, and it keeps them for
 * the next model.
 */
let dracoLoader = null;

/**
 * @return {DRACOLoader|null} The shared loader, or null without a decoder path.
 */
function getDracoLoader() {
    if (DRACO_DECODER_PATH === "") {
        return null;
    }
    if (!dracoLoader) {
        dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath(DRACO_DECODER_PATH);
    }
    return dracoLoader;
}

/**
 * A viewer for one 3D model.
 *
 * The viewer renders on demand. It draws a frame after a change and then
 * stops, so an open item card does not use the processor all the time.
 *
 * One instance holds one WebGL context. A browser allows only a small number
 * of contexts at the same time. Call dispose() when the viewer is no longer
 * visible.
 */
export class Model3dViewer {
    /**
     * @param {HTMLElement} stage Element that holds the canvas.
     * @param {Object} options
     * @param {function(string, number)} [options.onProgress] Gets the state
     *        ("loading", "ready", "error") and the progress from 0 to 1.
     * @param {function()} [options.onCameraChange] Runs after the camera moved.
     */
    constructor(stage, options = {}) {
        this.stage = stage;
        this.onProgress = options.onProgress || function () {
        };
        this.onCameraChange = options.onCameraChange || function () {
        };

        this.model = null;
        this.frameRequest = 0;
        this.disposed = false;
        this.currentLoad = null;

        this.createScene();
        this.observeSize();
    }

    /**
     * Build the renderer, the scene, the camera and the controls.
     */
    createScene() {
        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
            powerPreference: "high-performance"
        });
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;
        // Neutral tone mapping keeps the grey of metal grey. ACES shifts the
        // tones and makes bright metal look flat.
        this.renderer.toneMapping = THREE.NeutralToneMapping;
        this.renderer.toneMappingExposure = 1;
        this.renderer.domElement.classList.add("model3dCanvas");
        this.stage.appendChild(this.renderer.domElement);

        this.scene = new THREE.Scene();

        // A room environment gives metal surfaces their reflections and is the
        // only light of the scene. Extra lights would brighten the darker
        // reflections that make metal look like metal. The room is built in
        // the browser, so the page loads no image of an environment.
        this.pmrem = new THREE.PMREMGenerator(this.renderer);
        const room = new RoomEnvironment();
        this.environment = this.pmrem.fromScene(room, 0.04);
        room.dispose();
        this.scene.environment = this.environment.texture;

        this.camera = new THREE.PerspectiveCamera(40, 1, 0.01, 5000);
        this.camera.position.set(0, 0, 5);

        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.08;
        this.controls.enablePan = false;
        this.controls.minPolarAngle = THREE.MathUtils.degToRad(5);
        this.controls.maxPolarAngle = THREE.MathUtils.degToRad(175);
        this.controls.addEventListener("change", () => {
            this.invalidate();
            this.onCameraChange();
        });

        // The browser can take the context away, for example when it needs the
        // memory. Without these handlers the viewer then stays black.
        this.onContextLost = (event) => {
            event.preventDefault();
            this.stopFrame();
        };
        this.onContextRestored = () => {
            this.invalidate();
        };
        this.renderer.domElement.addEventListener("webglcontextlost", this.onContextLost);
        this.renderer.domElement.addEventListener("webglcontextrestored", this.onContextRestored);

        this.applySize();
    }

    /**
     * Follow the size of the stage. A resize observer also sees a change that
     * no window resize causes, for example the opening of the overlay.
     */
    observeSize() {
        this.resizeObserver = new ResizeObserver(() => this.applySize());
        this.resizeObserver.observe(this.stage);
    }

    /**
     * Give the renderer and the camera the current size of the stage.
     */
    applySize() {
        if (this.disposed) {
            return;
        }

        const width = Math.max(2, Math.floor(this.stage.clientWidth));
        const height = Math.max(2, Math.floor(this.stage.clientHeight));
        const devicePixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        const budget = Math.sqrt(MAX_RENDER_PIXELS / (width * height));
        const pixelRatio = Math.max(1, Math.min(devicePixelRatio, budget));

        this.renderer.setPixelRatio(pixelRatio);
        this.renderer.setSize(width, height, false);
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        this.invalidate();
    }

    /**
     * Load a model. A call replaces the model that the viewer shows now.
     *
     * @param {string} url Address of the GLB file.
     * @return {Promise<void>}
     */
    load(url) {
        this.removeModel();
        this.onProgress("loading", 0);

        const load = {url: url, cancelled: false};
        if (this.currentLoad) {
            this.currentLoad.cancelled = true;
        }
        this.currentLoad = load;

        const loader = new GLTFLoader();
        loader.setMeshoptDecoder(MeshoptDecoder);
        const draco = getDracoLoader();
        if (draco) {
            loader.setDRACOLoader(draco);
        }

        return new Promise((resolve, reject) => {
            loader.load(
                url,
                (gltf) => {
                    if (load.cancelled || this.disposed) {
                        this.disposeObject(gltf.scene);
                        resolve();
                        return;
                    }
                    this.addModel(gltf.scene);
                    this.onProgress("ready", 1);
                    resolve();
                },
                (event) => {
                    if (load.cancelled) {
                        return;
                    }
                    // A server that sends the file compressed reports no total
                    // size. The progress then stays unknown and the caller
                    // shows a message without a number.
                    const total = event.total || 0;
                    this.onProgress("loading", total > 0 ? event.loaded / total : 0);
                },
                (error) => {
                    if (load.cancelled) {
                        resolve();
                        return;
                    }
                    this.onProgress("error", 0);
                    reject(error);
                }
            );
        });
    }

    /**
     * Put a loaded model into the scene and frame it.
     *
     * @param {THREE.Object3D} object The scene of the loaded file.
     */
    addModel(object) {
        this.model = object;
        this.scene.add(object);

        object.traverse((child) => {
            if (!child.isMesh) {
                return;
            }
            // A file from a CAD program can come without normals. The material
            // is then black, because the light finds no direction to follow.
            if (child.geometry && !child.geometry.attributes.normal) {
                child.geometry.computeVertexNormals();
            }
        });

        this.frameModel();
        this.invalidate();
    }

    /**
     * Put the camera at a distance that shows the whole model.
     *
     * @param {boolean} keepAngles Keep the current direction of view.
     */
    frameModel(keepAngles = false) {
        if (!this.model) {
            return;
        }

        const box = this.getProductBox();
        if (box.isEmpty()) {
            return;
        }

        const sphere = box.getBoundingSphere(new THREE.Sphere());
        const radius = sphere.radius || 1;
        const verticalFov = THREE.MathUtils.degToRad(this.camera.fov);
        const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * (this.camera.aspect || 1));
        const distance = Math.max(
            (radius * FRAME_MARGIN) / Math.tan(verticalFov / 2),
            (radius * FRAME_MARGIN) / Math.tan(horizontalFov / 2)
        );

        let direction;
        if (keepAngles) {
            direction = this.camera.position.clone().sub(this.controls.target).normalize();
        } else {
            direction = new THREE.Vector3().setFromSpherical(new THREE.Spherical(
                1,
                THREE.MathUtils.degToRad(START_POLAR),
                THREE.MathUtils.degToRad(START_AZIMUTH)
            ));
        }

        this.controls.target.copy(sphere.center);
        this.camera.position.copy(sphere.center.clone().add(direction.multiplyScalar(distance)));
        this.camera.near = Math.max(distance / 1000, 0.001);
        this.camera.far = distance * 100;
        this.camera.updateProjectionMatrix();

        this.controls.minDistance = distance * 0.2;
        this.controls.maxDistance = distance * 4;
        this.controls.update();

        this.startDistance = distance;
    }

    /**
     * Measure the box around the item itself.
     *
     * The models come out of a studio scene and carry a floor below the item.
     * That floor is a plane of up to two metres, while an item measures a few
     * centimetres. A box around everything would therefore put the item in a
     * corner as a dot. The floor has no thickness, so a mesh that is flat in
     * one direction counts as scenery and not as part of the item.
     *
     * @return {THREE.Box3} Box around the item, or around the whole model when
     *         every mesh is flat.
     */
    getProductBox() {
        const productBox = new THREE.Box3();
        const meshBox = new THREE.Box3();

        this.model.updateMatrixWorld(true);
        this.model.traverse((child) => {
            if (!child.isMesh || !child.geometry) {
                return;
            }
            if (!child.geometry.boundingBox) {
                child.geometry.computeBoundingBox();
            }
            const bounds = child.geometry.boundingBox;
            if (!bounds || bounds.isEmpty()) {
                return;
            }
            const size = bounds.getSize(new THREE.Vector3());
            if (size.x <= 0 || size.y <= 0 || size.z <= 0) {
                return;
            }
            meshBox.copy(bounds).applyMatrix4(child.matrixWorld);
            productBox.union(meshBox);
        });

        return productBox.isEmpty() ? new THREE.Box3().setFromObject(this.model) : productBox;
    }

    /**
     * Turn the model around the upright axis.
     *
     * @param {number} degrees Angle from -180 to 180.
     */
    setAzimuth(degrees) {
        this.setAngles(degrees, this.getPolar());
    }

    /**
     * Move the camera above or below the model.
     *
     * @param {number} degrees Angle from 5 to 175.
     */
    setPolar(degrees) {
        this.setAngles(this.getAzimuth(), degrees);
    }

    /**
     * Put the camera at the given angles and keep the distance.
     *
     * @param {number} azimuth Angle around the upright axis, in degrees.
     * @param {number} polar Angle from the upright axis, in degrees.
     */
    setAngles(azimuth, polar) {
        const offset = this.camera.position.clone().sub(this.controls.target);
        const spherical = new THREE.Spherical().setFromVector3(offset);
        spherical.theta = THREE.MathUtils.degToRad(azimuth);
        spherical.phi = THREE.MathUtils.clamp(
            THREE.MathUtils.degToRad(polar),
            this.controls.minPolarAngle,
            this.controls.maxPolarAngle
        );
        this.camera.position.copy(this.controls.target.clone().add(new THREE.Vector3().setFromSpherical(spherical)));
        this.camera.lookAt(this.controls.target);
        this.controls.update();
        this.invalidate();
    }

    /**
     * @return {number} Current angle around the upright axis, in degrees.
     */
    getAzimuth() {
        return THREE.MathUtils.radToDeg(this.controls.getAzimuthalAngle());
    }

    /**
     * @return {number} Current angle from the upright axis, in degrees.
     */
    getPolar() {
        return THREE.MathUtils.radToDeg(this.controls.getPolarAngle());
    }

    /**
     * Move the camera towards the model or away from it.
     *
     * @param {number} factor Value below 1 moves nearer, above 1 moves away.
     */
    zoomBy(factor) {
        const offset = this.camera.position.clone().sub(this.controls.target);
        const distance = THREE.MathUtils.clamp(
            offset.length() * factor,
            this.controls.minDistance,
            this.controls.maxDistance
        );
        this.camera.position.copy(this.controls.target.clone().add(offset.setLength(distance)));
        this.controls.update();
        this.invalidate();
    }

    /**
     * Show the start view again.
     */
    reset() {
        this.frameModel(false);
        this.invalidate();
        this.onCameraChange();
    }

    /**
     * Ask for a new frame. More than one call before the next frame draws one
     * frame only.
     */
    invalidate() {
        if (this.disposed || this.frameRequest) {
            return;
        }
        this.frameRequest = requestAnimationFrame(() => {
            this.frameRequest = 0;
            this.controls.update();
            this.renderer.render(this.scene, this.camera);
        });
    }

    /**
     * Stop a frame that waits to be drawn.
     */
    stopFrame() {
        if (this.frameRequest) {
            cancelAnimationFrame(this.frameRequest);
            this.frameRequest = 0;
        }
    }

    /**
     * Take the model out of the scene and give its memory back.
     */
    removeModel() {
        if (!this.model) {
            return;
        }
        this.scene.remove(this.model);
        this.disposeObject(this.model);
        this.model = null;
    }

    /**
     * Give back the memory of all meshes below an object.
     *
     * @param {THREE.Object3D} object
     */
    disposeObject(object) {
        object.traverse((child) => {
            if (!child.isMesh) {
                return;
            }
            if (child.geometry) {
                child.geometry.dispose();
            }
            const materials = Array.isArray(child.material) ? child.material : [child.material];
            for (const material of materials) {
                if (!material) {
                    continue;
                }
                for (const value of Object.values(material)) {
                    if (value && value.isTexture) {
                        value.dispose();
                    }
                }
                material.dispose();
            }
        });
    }

    /**
     * Close the viewer and give back the WebGL context.
     */
    dispose() {
        if (this.disposed) {
            return;
        }
        this.disposed = true;

        if (this.currentLoad) {
            this.currentLoad.cancelled = true;
        }
        this.stopFrame();
        this.resizeObserver.disconnect();
        this.removeModel();

        this.renderer.domElement.removeEventListener("webglcontextlost", this.onContextLost);
        this.renderer.domElement.removeEventListener("webglcontextrestored", this.onContextRestored);

        this.controls.dispose();
        this.environment.texture.dispose();
        this.pmrem.dispose();
        this.renderer.dispose();
        this.renderer.forceContextLoss();

        if (this.renderer.domElement.parentNode) {
            this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
        }
    }
}
